import { DistributionRangeChart } from '@statsbomb/kitbag-datavis';
import { useTranslation } from 'react-i18next';
import { useDecimalPlaces } from '@/hooks/useDecimalPlaces';
import { hideDistributionTails } from '@/utils/distribution';
import { FormattedMetricDistributionBucket } from '@/utils/metrics';
import type { DistributionRowDatum } from './RadarDistributionList';

// Radar values for percentage metrics are already x100; the distribution data is not.
const toDistributionScale = (value: number, isPercentage?: boolean): number => (isPercentage ? value / 100 : value);
const fromDistributionScale = (value: number, isPercentage?: boolean): number => (isPercentage ? value * 100 : value);

// null tells the chart there is no recorded value; undefined means there is no comparison at all
const toChartValue = (value: number, hasValue: boolean | undefined, isPercentage?: boolean): number | null =>
  hasValue === false ? null : toDistributionScale(value, isPercentage);

const CHART_WIDTH = 300;
const CHART_HEIGHT = 40;

type RadarDistributionChartProps = {
  metric: DistributionRowDatum;
  distribution: FormattedMetricDistributionBucket;
};

export const RadarDistributionChart = ({ metric, distribution }: RadarDistributionChartProps) => {
  const { t } = useTranslation('radar');
  const formatNumberDecimals = useDecimalPlaces();

  const { min, max } = distribution;
  // the chart reads worst (left) to best (right); for reverseScale metrics the highest value is worst
  const [worst, best] = distribution.reverseScale ? [max, min] : [min, max];
  const formatChartValue = (value: number) =>
    formatNumberDecimals(metric.decimalPlaces, fromDistributionScale(value, metric.percentage));
  const range = { min: formatChartValue(min), max: formatChartValue(max) };

  return (
    <DistributionRangeChart
      chartWidth={CHART_WIDTH}
      chartHeight={CHART_HEIGHT}
      distributionData={hideDistributionTails(distribution.buckets)}
      worst={worst}
      best={best}
      value={toChartValue(metric.value, metric.hasValue, metric.percentage)}
      comparisonValue={
        metric.comparisonValue === undefined
          ? undefined
          : toChartValue(metric.comparisonValue, metric.comparisonHasValue, metric.percentage)
      }
      formatValue={formatChartValue}
      // the chart is hidden from assistive technology, so this text carries the value as well as the range
      rangeDescription={
        metric.hasValue === false
          ? t('distributionView.noValueDescription', range)
          : t('distributionView.valueDescription', {
              ...range,
              value: formatNumberDecimals(metric.decimalPlaces, metric.value),
            })
      }
      preserveAspectRatio="none"
    />
  );
};
